import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Loader, InfiniteListMessage } from '@pwc/pwc-deals-ui';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import InfiniteScroll from 'react-infinite-scroll-component';

import { USER_ROLES } from '@pwc/pwc-deals-web-common';
import { getUserRole } from 'common/redux/security/loggedInUser/loggedInUserSlice';
import { isProjectListingEnabled } from 'common/helpers/featureFlags';
import FilterDropdown from 'common/components/FilterDropdown/FilterDropdown';
import { isUSTerritory } from 'common/helpers/projectInitUtil';
import { FAVOURITE, US_FAVOURITE } from 'common/constants/General';
import {
  getPagedProjects,
  getPagedProjectsSelector,
  getProjectListCount,
  getFavouriteProjectList,
  getFavProjects,
  getFavProjectsCount,
  getAllProjectCount,
  isFavLoading,
  getSearchLoading,
  getSearchPagedProjects,
  getSearchPagedProjectsSelector,
  getSearchTotal,
  getSearchCurrentPage,
  clearText,
  getCountPagedProjects,
  getSearchCount,
} from './pagedProjectsSlice';
import EmptySearchResult from './EmptySearchResult';
import EmptyFavouriteCard from './EmptyFavouriteCard';
import ProjectListRow from './ProjectListRow';
import EmptyProjectPage from './EmptyProjectPage';
import EmptyClientProjectPage from './EmptyClientProjectPage';
import ProjectFilter from './ProjectFilter';
import useTabletOrMobile from '../../common/hooks/useTabletOrMobile';
import { projectStates, clientProjectStates } from './ProjectFilterConsts';
import authorizationService from '../../common/services/security/authorizationService';
import './ProjectList.scss';
import { ProjectSwitchBanner } from '../../common/components/Banner';

const items = (t) =>
  Object.keys(projectStates).map((key) => ({
    id: projectStates[key],
    name: t(key),
  }));

const clientViewOptions = (t) =>
  Object.keys(clientProjectStates).map((key) => ({
    id: projectStates[key],
    name: t(key),
  }));

const ProjectList = () => {
  const { t } = useTranslation();
  const [isProjectsEmpty, setIsProjetcsEmpty] = useState(false);
  const dispatch = useDispatch();
  const isPwcUser = useSelector(getUserRole) === USER_ROLES.PWC_USER;
  const isClientUser = useSelector(getUserRole) === USER_ROLES.CLIENT;
  const isThirdPartyMember =
    useSelector(getUserRole) === USER_ROLES.THIRD_PARTY;

  // This feature flag will be removed when the complete feature is being delivered
  const filterState = isProjectListingEnabled
    ? projectStates['po.navtopbar.selectactive']
    : projectStates['po.navtopbar.selectall'];
  const [selectedProjectState, setSelectedProjectState] = useState(filterState);

  const [isFilterChangedInSearch, setIsFilterChangedInSearch] = useState(false);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchString, setSearchString] = useState('');
  const isTabletOrMobile = useTabletOrMobile();
  const favProjects = useSelector(getFavProjects);
  const favProjectsCount = useSelector(getFavProjectsCount);
  const allProjectCount = useSelector(getAllProjectCount);
  const favLoading = useSelector(isFavLoading);

  const { loading, projects, pageSize, total, currentPage, hasItems } =
    useSelector(getPagedProjectsSelector);

  const searchedProjects = useSelector(getSearchPagedProjectsSelector);
  const searchLoading = useSelector(getSearchLoading);
  const searchTotal = useSelector(getSearchTotal);
  const searchCurrentPage = useSelector(getSearchCurrentPage);
  const totalSearchCount = useSelector(getSearchCount);

  const hasSearchMoreItem = useCallback(() => {
    return searchTotal > searchedProjects.length;
  }, [searchedProjects, searchTotal]);

  const hasMoreItems = useCallback(() => {
    return total > projects.length;
  }, [projects, total]);

  const fetchPagedProjects = useCallback(
    (page) => {
      dispatch(
        getPagedProjects({
          page,
          pageSize,
          filter: selectedProjectState,
        })
      );
    },
    [dispatch, pageSize, selectedProjectState]
  );

  const fetchPagedSearchProjects = useCallback(
    (page, filter = selectedProjectState) => {
      dispatch(
        getSearchPagedProjects({
          page,
          pageSize: 10,
          filter,
          searchString,
        })
      );
    },
    [selectedProjectState, searchString, dispatch]
  );

  const handleNext = () => !loading && fetchPagedProjects(currentPage + 1);

  const handleSearchNext = () =>
    !searchLoading && fetchPagedSearchProjects(searchCurrentPage + 1);

  const setProjectState = (projectState) => {
    if (!isSearchActive) {
      dispatch(getPagedProjects({ page: 1, pageSize, filter: projectState }));
    } else {
      fetchPagedSearchProjects(1, projectState);
      dispatch(
        getCountPagedProjects({
          page: 1,
          pageSize: 10,
          filter: projectState,
          searchString,
        })
      );
      setIsFilterChangedInSearch(true);
    }
    setSelectedProjectState(projectState);
  };

  useEffect(() => {
    if (isProjectListingEnabled) {
      dispatch(getFavouriteProjectList({ filter: selectedProjectState }));
      dispatch(getProjectListCount({ filter: selectedProjectState }));
    }
  }, [selectedProjectState, dispatch]);

  useEffect(() => {
    if (!loading && !hasItems) {
      setIsProjetcsEmpty(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, hasItems]);

  useEffect(() => {
    fetchPagedProjects(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isSearchActive && isFilterChangedInSearch) {
      fetchPagedProjects(1);
      setIsFilterChangedInSearch(false);
    }
  }, [isSearchActive, isFilterChangedInSearch, fetchPagedProjects]);

  useEffect(() => {
    if (searchString !== '') {
      fetchPagedSearchProjects(1);
      dispatch(
        getCountPagedProjects({
          page: 1,
          pageSize: 10,
          filter: selectedProjectState,
          searchString,
        })
      );
    }
  }, [
    searchString,
    dispatch,
    fetchPagedSearchProjects,
    selectedProjectState,
    isPwcUser,
  ]);

  const endOfListComponent = useCallback(
    () => (
      <div className="no-more-items">
        <InfiniteListMessage
          iconName="confirmed"
          iconClass="outline icon-grey-list-label end-of-infite-list-message__icon"
          message={t('NoMoreItems')}
          iconSize={{ width: 24, height: 24 }}
        />
      </div>
    ),
    // eslint-disable-next-line
    []
  );

  const projectFilter = useMemo(() => {
    return (
      <div className="viewFlexContainer">
        <div className="viewLabel">{t('view')} </div>
        <ProjectFilter
          items={
            isThirdPartyMember || isClientUser ? clientViewOptions(t) : items(t)
          }
          selectedItemId={selectedProjectState}
          selectItem={setProjectState}
        />
      </div>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClientUser, isThirdPartyMember, selectedProjectState, t]);

  if (authorizationService.canCreateProjects() && isProjectsEmpty) {
    return <EmptyProjectPage />;
  }
  if ((isThirdPartyMember || isClientUser) && isProjectsEmpty) {
    return (
      <>
        <div className="projectListHeader">
          <h2>{t('yourProject')}</h2>
        </div>
        <EmptyClientProjectPage />
      </>
    );
  }

  const ScrollLoader = () => (
    <>
      {(loading || favLoading || searchLoading) && (
        <Loader
          className={isTabletOrMobile ? 'small' : ''}
          data-testid="infite-scroll-loader"
        />
      )}
    </>
  );

  const onEnter = (searchStr) => {
    setIsSearchActive(true);
    setSearchString(searchStr);
  };

  const onBlur = (searchStr) => {
    setSearchString(searchStr);
    setIsSearchActive(true);
  };

  const onCancel = () => {
    setIsSearchActive(false);
    setSearchString('');
    dispatch(clearText());
  };

  return (
    <div data-testid="project-container">
      {isProjectListingEnabled ? (
        <div className="projectListHeader">
          <h2>{t('yourProjects')}</h2>
          <ProjectSwitchBanner />
          <div>
            <div className="search-container">
              <div className="searchFlexContainer">
                <div className="searchLabel">{t('searchProjects')}</div>
                <FilterDropdown
                  minChars={3}
                  name="Search"
                  cancelIcon
                  onChange={() => {}}
                  onEnter={onEnter}
                  onCancel={onCancel}
                  onBlur={onBlur}
                  placeholder={
                    isPwcUser
                      ? t('ProjectClientEngagementLeadOrManager')
                      : t('projectName')
                  }
                />
              </div>
              {projectFilter}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="project-list-header-old">
            <h2>{t('projects')}</h2>
            <ProjectSwitchBanner />
            {isPwcUser && (
              <ProjectFilter
                items={items(t)}
                selectedItemId={selectedProjectState}
                selectItem={setProjectState}
              />
            )}
          </div>
          <div className="project-container">
            <InfiniteScroll
              dataLength={projects.length}
              hasMore={hasMoreItems()}
              next={handleNext}
              scrollThreshold={0.75}
            >
              {projects.map((project) => (
                <ProjectListRow key={project.id} project={project} />
              ))}
            </InfiniteScroll>
            <ScrollLoader />
            {!loading && hasItems && total >= (projects || []).length && (
              <>{endOfListComponent()}</>
            )}
          </div>
        </>
      )}
      {!isSearchActive && isProjectListingEnabled && (
        <div className="project-container">
          <div className="favLabel">
            {isUSTerritory ? t(US_FAVOURITE) : t(FAVOURITE)} ({favProjectsCount}
            )
          </div>
          {favProjectsCount === 0 && <EmptyFavouriteCard />}
          {favProjects.map((project) => (
            <ProjectListRow
              key={project.id}
              project={project}
              isFavourite
              isSearch={false}
            />
          ))}

          <div className="allLabel">
            {t('all')} ({allProjectCount})
          </div>
          <InfiniteScroll
            dataLength={projects.length}
            hasMore={hasMoreItems()}
            next={handleNext}
            scrollThreshold={0.75}
          >
            {projects.map((project) => (
              <ProjectListRow
                key={project.id}
                project={project}
                isFavourite={false}
                isSearch={false}
              />
            ))}
          </InfiniteScroll>

          <ScrollLoader />

          {!loading && hasItems && total >= (projects || []).length && (
            <>{endOfListComponent()}</>
          )}
        </div>
      )}
      {isSearchActive && (
        <div className="project-container">
          <div className="searchHeader">
            {t('searchResults')} ({totalSearchCount}){' '}
          </div>
          {!searchLoading && totalSearchCount === 0 && (
            <EmptySearchResult searchedString={searchString} />
          )}
          <InfiniteScroll
            dataLength={searchedProjects.length}
            hasMore={hasSearchMoreItem()}
            next={handleSearchNext}
            scrollThreshold={0.75}
          >
            {searchedProjects.map((project) => (
              <ProjectListRow
                key={project.id}
                project={project}
                isSearchActive
                isFavourite={false}
                isSearch
              />
            ))}
          </InfiniteScroll>
          <ScrollLoader />
          {!searchLoading && searchTotal >= (searchedProjects || []).length && (
            <>{endOfListComponent()}</>
          )}
        </div>
      )}
    </div>
  );
};

export default ProjectList;
